import React, { useMemo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DescriptionOutlined, UploadFileRounded } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import CustomPreparationDocuments from './CustomPreparationDocuments';

const normalize = (value = '') => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const genericWords = new Set(['a', 'an', 'and', 'current', 'document', 'documents', 'latest', 'or', 'previous', 'recent', 'report', 'the']);
const meaningfulWords = (value) => normalize(value).split(' ').filter((word) => word.length > 2 && !genericWords.has(word));

const evidenceText = (item = {}) => [
  item.title, item.label, item.documentType, item.name, item.summary, item.text,
].map(normalize).filter(Boolean).join(' ');

export const findAvailableSuggestedDocument = (suggestion, evidence = []) => {
  const words = meaningfulWords(suggestion?.label);
  if (!words.length) return null;
  return evidence.find((item) => item.sourceType === 'document' && words.some((word) => evidenceText(item).includes(word))) || null;
};

const CustomJourneyHelpfulDocuments = ({
  suggestions = [], evidence = [], canAdd, accent, onUpload, onOpenDocument,
  childId = '', draftId = '', preparationDocuments = [],
}) => {
  const rows = useMemo(() => suggestions.map((suggestion) => ({
    suggestion,
    available: findAvailableSuggestedDocument(suggestion, evidence),
  })), [evidence, suggestions]);
  if (!rows.length) return null;

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: '22px', border: `1px solid ${alpha(accent.primary, 0.18)}` }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DescriptionOutlined sx={{ color: accent.ink }} />
        <Box>
          <Typography component="h2" sx={{ fontWeight: 800 }}>Documents that may help</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.62) }}>
            Optional suggestions for this preparation. You can continue without them.
          </Typography>
        </Box>
      </Stack>
      <Stack spacing={0.9} sx={{ mt: 1.2 }}>
        {rows.map(({ suggestion, available }) => (
          <Box key={suggestion.id} sx={{ display: { xs: 'block', sm: 'grid' }, gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, p: 1.1, borderRadius: '14px', bgcolor: alpha(accent.primary, 0.045) }}>
            <Box>
              <Typography sx={{ fontWeight: 750 }}>{suggestion.label}</Typography>
              <Typography sx={{ mt: 0.15, fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.62) }}>{suggestion.reason}</Typography>
              <Typography sx={{ mt: 0.35, fontSize: '0.75rem', fontWeight: 750, color: available ? accent.ink : alpha(colors.semantic.ink, 0.52) }}>
                {available ? 'Available in Lifelog' : 'Not added yet'}
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={available ? <DescriptionOutlined /> : <UploadFileRounded />}
              disabled={!available && !canAdd}
              onClick={() => available ? onOpenDocument?.(available) : onUpload?.(suggestion)}
              sx={{ mt: { xs: 0.7, sm: 0 }, alignSelf: 'center', justifySelf: 'end' }}
            >
              {available ? 'Review' : 'Upload'}
            </Button>
          </Box>
        ))}
      </Stack>
      {canAdd && childId && draftId ? (
        <Box sx={{ mt: 1.2 }}>
          <CustomPreparationDocuments
            childId={childId}
            draftId={draftId}
            initialDocuments={preparationDocuments}
          />
        </Box>
      ) : null}
    </Paper>
  );
};

export default CustomJourneyHelpfulDocuments;
